import { React, useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { downloadBlob } from '../../utils/csvExport';
import { formatMonthYearLabelPtBr, formatShortMonthParam } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/speReport.css';

const RelatorioSPEs = () => {
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [dateTimePeriodo, setDateTimePeriodo] = useState(null);
    const [errorPeriodo, setErrorPeriodo] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitRelatorioSPEs (e) {
        e.preventDefault();

        if (!dateTimePeriodo) {
            setErrorPeriodo(true);
            return;
        }

        setErrorPeriodo(false);
        setIsSubmit(true);

        const periodo = formatShortMonthParam(dateTimePeriodo);
        const body = JSON.stringify({
            periodo
        });

        try {
            const response = await fetchFlask('criarRelatorioSPEs', body, true);

            if (response instanceof Blob && response.type.includes('application/json')) {
                const errorResponse = JSON.parse(await response.text());
                setMessage(errorResponse.message);
                setStatus(errorResponse.status);
                setOpenAlerta(true);
                return;
            }

            if (response instanceof Blob) {
                downloadBlob(`relatorio_spes_${periodo}.csv`, response);

                setMessage('Relatório CSV gerado com sucesso.');
                setStatus('success');
                setOpenAlerta(true);
                return;
            }

            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
        } finally {
            setIsSubmit(false);
        }
    }

    const periodoLabel = formatMonthYearLabelPtBr(dateTimePeriodo);
    const completedFields = [dateTimePeriodo].filter(Boolean).length;

    return (
        <Box className='mainBox spe-report-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Relatório SPEs</PageHeader>

            <form method='POST' onSubmit={submitRelatorioSPEs} className='spe-report-layout'>
                <Box className='spe-report-workspace'>
                    <Box className='spe-report-intro'>
                        <Box>
                            <span className='spe-report-kicker'>O&M</span>
                            <h2>Relatório Mensal de geração das SPEs</h2>
                        </Box>
                        <p>
                            Selecione o mês de competência para gerar o arquivo consolidado do período.
                        </p>
                    </Box>

                    <Box className='spe-report-panel'>
                        <Box className='spe-report-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Competência</span>
                                <strong>Período do relatório</strong>
                            </Box>
                        </Box>

                        <FormControl className='spe-report-date-control' error={errorPeriodo}>
                            <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    id='data'
                                    label='Período'
                                    views={['month', 'year']}
                                    openTo='month'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTimePeriodo}
                                    onChange={(value) => {
                                        setDateTimePeriodo(value);
                                        setErrorPeriodo(false);
                                    }}
                                    slotProps={{
                                        openPickerButton: {
                                            color: 'primary'
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                            {errorPeriodo && (
                                <FormHelperText>Por favor selecione um período.</FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    <Box className='spe-report-export-strip'>
                        <TableChartRoundedIcon />
                        <Box>
                            <span>Arquivo gerado</span>
                            <strong>{`relatorio_spes_${dateTimePeriodo ? formatShortMonthParam(dateTimePeriodo) : 'periodo'}.csv`}</strong>
                        </Box>
                    </Box>

                    <Box className='spe-report-rail'>
                        <Box>
                            <DescriptionRoundedIcon />
                            <span>Formato</span>
                            <strong>CSV</strong>
                        </Box>
                        <Box>
                            <InsightsRoundedIcon />
                            <span>Escopo</span>
                            <strong>SPEs do período</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='spe-report-summary'>
                    <Box className='spe-report-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da geração</strong>
                        </Box>
                    </Box>

                    <Box className='spe-report-summary-list'>
                        <Box>
                            <span>Competência</span>
                            <strong className={!dateTimePeriodo ? 'is-empty' : undefined}>
                                {periodoLabel}
                            </strong>
                        </Box>
                        <Box>
                            <span>Entrega</span>
                            <strong>Download automático</strong>
                        </Box>
                        <Box>
                            <span>Formato</span>
                            <strong>CSV consolidado</strong>
                        </Box>
                    </Box>

                    <Box className='spe-report-attention'>
                        <SummarizeRoundedIcon />
                        <p>
                            A geração usa a competência selecionada e retorna o arquivo para conferência operacional.
                        </p>
                    </Box>

                    <Box className='spe-report-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/1 preenchido</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<TableChartRoundedIcon />}>
                        Gerar CSV
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default RelatorioSPEs;
